import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { makeStyles } from '@material-ui/core/styles'
import clsx from 'clsx'

// Visual chrome mirrors SydInput so this textarea reads as part of the same
// Syd common-design family without forking SydInput (which is single-line).
// Same focus outline, border token, and surface fill — only the inner element
// changes from <input> to <textarea> + monospace font for JSON readability.
const useStyles = makeStyles(() => ({
  wrapper: {
    width: '100%',
    minWidth: 0,
    border: 'var(--border-neutral-thin)',
    borderRadius: 'var(--border-radius-sm)',
    backgroundColor: 'var(--tc-input-base, var(--color-surface-050))',
    display: 'flex',
    flexDirection: 'column',
    transition: 'outline 200ms ease-in-out',
    outline: '1px solid transparent',
    '&:focus-within': {
      outline: '1px solid var(--color-primary-500)'
    }
  },
  textarea: {
    width: '100%',
    border: 'none',
    outline: 'none',
    backgroundColor: 'transparent',
    color: 'var(--text-color-main)',
    fontFamily: 'var(--monospace-font, monospace)',
    fontSize: 'var(--font-size-xs)',
    padding: '8px 10px',
    resize: 'vertical',
    minHeight: '80px',
    '&::placeholder': {
      opacity: '0.4',
      fontFamily: 'var(--root-font)'
    }
  },
  error: {
    color: 'var(--color-decline-500)',
    fontSize: 'var(--font-size-xxs, 0.625rem)',
    marginTop: '2px'
  }
}))

const isPlainObject = (value) => !!value && typeof value === 'object' && !Array.isArray(value)

function JsonField ({ value, onChange, placeholder, rows, expectObject, className }) {
  const classes = useStyles()
  const [text, setText] = useState(value == null ? '' : JSON.stringify(value, null, 2))
  const [parseError, setParseError] = useState(null)

  useEffect(() => {
    setText(value == null ? '' : JSON.stringify(value, null, 2))
  }, [value])

  const handleChange = (e) => {
    const next = e.target.value
    setText(next)
    if (!next.trim()) {
      setParseError(null)
      onChange(null)
      return
    }
    try {
      const parsed = JSON.parse(next)
      if (expectObject && !isPlainObject(parsed)) {
        setParseError('Value must be a JSON object (e.g. { "key": "value" })')
        return
      }
      setParseError(null)
      onChange(parsed)
    } catch (err) {
      setParseError(err.message)
    }
  }

  return (
    <div>
      <div className={clsx(classes.wrapper, className)}>
        <textarea
          className={classes.textarea}
          value={text}
          onChange={handleChange}
          placeholder={placeholder}
          rows={rows ?? 4}
        />
      </div>
      {parseError ? (
        <div className={classes.error}>{parseError}</div>
      ) : null}
    </div>
  )
}

JsonField.propTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  rows: PropTypes.number,
  expectObject: PropTypes.bool,
  className: PropTypes.string
}

export default JsonField
